/** SHEIN 平台工作区路由常量（店铺类型路径段 + 公用页面） */

import { openUrlInNewTab } from '@/config/ipeRoutes';
import { bindNavLabels } from '@/i18n/bindCopy';
import { normalizeSheinShopType } from '@/utils/platformShopCatalog';

export const IPE_SHEIN_BASE_PATH = '/ipe/shein';
export const IPE_SHEIN_GATE_PATH = `${IPE_SHEIN_BASE_PATH}/gate`;
/** 通知帮助中心：平台/系统通知 + 常见问题 + 问题反馈（新标签打开） */
export const SHEIN_NOTICE_HELP_PATH = `${IPE_SHEIN_BASE_PATH}/help`;

/** URL 类型段 slug：全托管 / 半托管 / 自运营 */
export const SHEIN_SHOP_TYPE_SLUGS = ['full', 'semi', 'self'];

/** 产品管理二级菜单（挂在侧栏「产品管理」下） */
export const SHEIN_PRODUCT_SUBNAV = [
  { id: 'publish', label: '发布产品', path: 'publish', openInNewTab: true },
  { id: 'info', label: '产品信息', path: '' },
  { id: 'records', label: '发布记录', path: 'records' },
  { id: 'drafts', label: '草稿箱', path: 'drafts' },
];

/** 合规资质二级菜单（待办已统一到首页） */
export const SHEIN_COMPLIANCE_SUBNAV = [
  { id: 'product-qual', label: '产品合规', path: 'product-qual' },
  { id: 'materials', label: '合规资料', path: 'materials' },
];

/** 模板库二级菜单 */
export const SHEIN_TEMPLATE_SUBNAV = [
  { id: 'product', label: '产品模板', path: 'product' },
  { id: 'size-chart', label: '尺码库', path: 'size-chart' },
  { id: 'attributes', label: '属性库', path: 'attributes' },
  { id: 'photo', label: '实拍图库', path: 'photo' },
  { id: 'certificates', label: '证书库', path: 'certificates' },
  { id: 'rrp', label: '建议价资料库', path: 'rrp' },
];

/** 价格处理二级菜单 */
export const SHEIN_PRICE_SUBNAV = [
  { id: 'cost', label: '议价单', path: 'cost' },
  { id: 'rrp', label: '建议零售价', path: 'rrp' },
];

/** 营销活动二级菜单 */
export const SHEIN_MARKETING_SUBNAV = [
  { id: 'activities', label: '活动列表', path: '' },
  { id: 'enrolls', label: '报名记录', path: 'enrolls' },
];

/** 订单管理二级菜单 */
export const SHEIN_ORDER_SUBNAV = [
  { id: 'list', label: '订单列表', path: '' },
  { id: 'returns', label: '退货退款', path: 'returns' },
];

export const SHEIN_NAV_ITEMS = [
  { id: 'home', label: '首页', path: 'home' },
  {
    id: 'products',
    label: '产品管理',
    path: 'products',
    children: SHEIN_PRODUCT_SUBNAV,
  },
  {
    id: 'discuss',
    label: '价格处理',
    path: 'discuss',
    children: SHEIN_PRICE_SUBNAV,
  },
  {
    id: 'marketing',
    label: '营销活动',
    path: 'marketing',
    children: SHEIN_MARKETING_SUBNAV,
  },
  {
    id: 'compliance',
    label: '合规资质',
    path: 'compliance',
    children: SHEIN_COMPLIANCE_SUBNAV,
  },
  {
    id: 'templates',
    label: '模板库',
    path: 'templates',
    children: SHEIN_TEMPLATE_SUBNAV,
  },
  {
    id: 'orders',
    label: '订单管理',
    path: 'orders',
    children: SHEIN_ORDER_SUBNAV,
  },
  { id: 'finance', label: '财务账单', path: 'finance' },
  { id: 'shop', label: '店铺信息', path: 'shop' },
];

bindNavLabels([
  SHEIN_PRODUCT_SUBNAV,
  SHEIN_COMPLIANCE_SUBNAV,
  SHEIN_TEMPLATE_SUBNAV,
  SHEIN_PRICE_SUBNAV,
  SHEIN_MARKETING_SUBNAV,
  SHEIN_ORDER_SUBNAV,
  SHEIN_NAV_ITEMS,
]);

export const SHEIN_ACTIVE_SHOP_STORAGE_KEY = 'ipe_shein_active_shop_id';
export const SHEIN_WORKSPACE_TOKEN_KEY = 'ipe_shein_workspace_token';
export const SHEIN_WORKSPACE_SHOP_TYPE_KEY = 'ipe_shein_workspace_shop_type';
export const SHEIN_SHOP_SWITCH_TOAST_KEY = 'ipe_shein_shop_switch_toast';

export function isSheinShopAuthorized(shop) {
  if (!shop || shop.authStatus !== 'connected') return false;
  if (shop.mockBinding === true) return false;
  if (shop.realAuthBound === false) return false;
  return true;
}

/** @param {string} [shopType] @returns {'full'|'semi'|'self'|''} */
export function shopTypeSlugForLabel(shopType) {
  const normalized = normalizeSheinShopType(shopType);
  if (normalized === '全托管') return 'full';
  if (normalized === '半托管') return 'semi';
  if (normalized === '自运营') return 'self';
  return '';
}

/** @param {string} [slug] @returns {'全托管'|'半托管'|'自运营'|''} */
export function shopTypeLabelForSlug(slug) {
  const s = String(slug || '').trim().toLowerCase();
  if (s === 'full') return '全托管';
  if (s === 'semi') return '半托管';
  if (s === 'self') return '自运营';
  return '';
}

export function normalizeSheinShopTypeSlug(slug) {
  const label = shopTypeLabelForSlug(slug);
  return label ? shopTypeSlugForLabel(label) : '';
}

export function isSheinShopTypeSlug(slug) {
  return Boolean(normalizeSheinShopTypeSlug(slug));
}

export function parseSheinShopTypeSlugFromPath(pathname = '') {
  const p = String(pathname || '');
  const prefix = `${IPE_SHEIN_BASE_PATH}/`;
  if (!p.startsWith(prefix)) return '';
  const rest = p.slice(prefix.length);
  const first = rest.split('/')[0] || '';
  if (first === 'gate' || first === 'help') return '';
  return normalizeSheinShopTypeSlug(first);
}

function resolveShopTypeSlug(shopTypeOrSlug) {
  if (isSheinShopTypeSlug(shopTypeOrSlug)) {
    return normalizeSheinShopTypeSlug(shopTypeOrSlug);
  }
  const fromLabel = shopTypeSlugForLabel(shopTypeOrSlug);
  if (fromLabel) return fromLabel;
  if (typeof window !== 'undefined') {
    const fromPath = parseSheinShopTypeSlugFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(SHEIN_WORKSPACE_SHOP_TYPE_KEY);
      const fromStored = shopTypeSlugForLabel(stored);
      if (fromStored) return fromStored;
    }
  } catch {
    /* ignore */
  }
  return 'semi';
}

/**
 * 构建 SHEIN 工作区路径。
 * - buildSheinPath('home') — 从当前 URL / storage 推断类型
 * - buildSheinPath('full', 'home') — 显式类型
 */
export function buildSheinPath(shopTypeOrSegment = '', maybeSegment) {
  let slug;
  let segment;
  if (maybeSegment !== undefined) {
    slug = resolveShopTypeSlug(shopTypeOrSegment);
    segment = maybeSegment;
  } else if (isSheinShopTypeSlug(shopTypeOrSegment) && !String(shopTypeOrSegment).includes('/')) {
    slug = normalizeSheinShopTypeSlug(shopTypeOrSegment);
    segment = '';
  } else {
    slug = resolveShopTypeSlug('');
    segment = shopTypeOrSegment;
  }
  const base = `${IPE_SHEIN_BASE_PATH}/${slug || 'semi'}`;
  if (!segment) return base;
  return `${base}/${String(segment).replace(/^\//, '')}`;
}

/**
 * SHEIN 门禁/登录页路径；可带 shopId 预选店铺（已登录且类型匹配时门禁会直达工作区）。
 * @param {{ shopId?: string|number, shopType?: string, redirect?: string }} [options]
 */
export function buildSheinGatePath({ shopId, shopType, redirect } = {}) {
  const params = new URLSearchParams();
  if (shopId != null && String(shopId).trim() !== '') {
    params.set('shopId', String(shopId).trim());
  }
  if (shopType) {
    const label = normalizeSheinShopType(shopType) || shopTypeLabelForSlug(shopType) || String(shopType);
    if (label) params.set('shopType', label);
  }
  if (redirect) {
    params.set('redirect', String(redirect));
  }
  const qs = params.toString();
  return qs ? `${IPE_SHEIN_GATE_PATH}?${qs}` : IPE_SHEIN_GATE_PATH;
}

/** 新标签打开 SHEIN 门禁，并尽量带出指定店铺 */
export function openSheinGateForShop(shopId, options = {}) {
  ensureSheinWorkspaceTokenShared();
  const path = buildSheinGatePath({
    shopId,
    shopType: options.shopType,
    redirect: options.redirect,
  });
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

/** 侧栏分组子路径：parentPath + childPath → /ipe/shein/{type}/{parent}/{child} */
export function buildSheinChildPath(parentPath = '', childPath = '', shopTypeOrSlug = '') {
  const parent = String(parentPath || '').replace(/^\/+|\/+$/g, '');
  const slugArg = shopTypeOrSlug || undefined;
  if (!parent) {
    return slugArg !== undefined ? buildSheinPath(slugArg, childPath) : buildSheinPath(childPath);
  }
  if (!childPath) {
    return slugArg !== undefined ? buildSheinPath(slugArg, parent) : buildSheinPath(parent);
  }
  const seg = `${parent}/${String(childPath).replace(/^\//, '')}`;
  return slugArg !== undefined ? buildSheinPath(slugArg, seg) : buildSheinPath(seg);
}

export function buildSheinProductChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildSheinChildPath('products', childPath, shopTypeOrSlug);
}

export function buildSheinComplianceChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildSheinChildPath('compliance', childPath, shopTypeOrSlug);
}

export function buildSheinTemplateChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildSheinChildPath('templates', childPath, shopTypeOrSlug);
}

/** 将工作区 token 同步到 localStorage，供新标签页复用（sessionStorage 不跨标签） */
export function ensureSheinWorkspaceTokenShared() {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(SHEIN_WORKSPACE_TOKEN_KEY)) return;
    if (typeof sessionStorage === 'undefined') return;
    const fromSession = sessionStorage.getItem(SHEIN_WORKSPACE_TOKEN_KEY);
    if (!fromSession) return;
    localStorage.setItem(SHEIN_WORKSPACE_TOKEN_KEY, fromSession);
    sessionStorage.removeItem(SHEIN_WORKSPACE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** 在新标签打开 SHEIN 路径（发布/编辑页等） */
export function openSheinPathInNewTab(segmentOrPath = '') {
  ensureSheinWorkspaceTokenShared();
  const path = String(segmentOrPath || '').startsWith('/')
    ? String(segmentOrPath)
    : buildSheinPath(segmentOrPath);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

export function isSheinPlatformPath(pathname = '') {
  const p = pathname || '';
  return p === IPE_SHEIN_BASE_PATH || p.startsWith(`${IPE_SHEIN_BASE_PATH}/`);
}
